import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home, PenLine, LogOut, User, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { cn } from '../utils/cn';
import logo from '../assets/logo.png';

const Navigation = ({ user, onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // For now, assume all users are admins - this would normally be based on a user role
  const isAdmin = !!user;

  useEffect(() => {
    // Close mobile menu when route changes
    setMenuOpen(false);
  }, [location]);
  

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    onLogout();
    navigate('/login');
  };

  // Helper function to get display name (prefer username over email)
  const getDisplayName = () => {
    if (!user) return 'Player';
    
    // Check if username looks like an email address (contains @)
    if (user.username && user.username.includes('@')) {
      // It's an email, return just the part before @
      return user.username.split('@')[0];
    }
    
    // Otherwise return username or fallback to Player
    return user.username || 'Player';
  };

  // Get initial for avatar
  const getInitial = () => {
    if (!user) return 'U';
    
    // If username is an email, use first letter of the part before @
    if (user.username && user.username.includes('@')) {
      return user.username.split('@')[0].charAt(0).toUpperCase();
    }
    
    return user.username?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-background/95 via-background to-background/95 backdrop-blur-md border-b border-border shadow-sm pt-safe pl-safe pr-safe">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <Link to="/" className="flex items-center space-x-3 transition-transform hover:scale-105">
            <img 
              src={logo} 
              alt="KΣ Die Logo" 
              className="h-10 w-10 object-contain" 
            />
            <span className="font-bold text-xl text-primary hidden sm:inline">KΣ Die</span>
          </Link>

          {user && (
            <div className="hidden md:flex items-center space-x-3">
              <div className="flex items-center px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                <User className="h-4 w-4 text-primary mr-2" />
                <span className="text-sm font-medium">
                  {getDisplayName()}
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="border-primary/20 hover:bg-primary/10">
                Logout
              </Button>
            </div>
          )}

          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={toggleMenu} aria-label="Toggle Menu" className="hover:bg-primary/10">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          <div className={cn(
            "fixed inset-0 z-50 bg-background/95 backdrop-blur-md transition-transform duration-300 md:hidden",
            menuOpen ? "translate-x-0" : "translate-x-full"
          )}>
            <div className="flex flex-col h-full p-6">
              <div className="flex items-center justify-between mb-8">
                <span className="font-bold text-xl text-primary">Menu</span>
                <Button variant="ghost" size="icon" onClick={toggleMenu} className="hover:bg-primary/10">
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex items-center mb-8 justify-center">
                <div className="flex flex-col items-center">
                  <img 
                    src={logo} 
                    alt="KΣ Die Logo" 
                    className="h-24 w-24 object-contain mb-3 drop-shadow-md" 
                  />
                  <span className="font-bold text-xl text-primary">KΣ Die</span>
                </div>
              </div>

              <div className="space-y-6 flex-1">
                {user ? (
                  <>
                    <div className="flex items-center space-x-3 mb-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                      <Avatar className="border-2 border-primary">
                        <AvatarFallback className="bg-primary/20 text-primary font-semibold">
                          {getInitial()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="font-medium">{getDisplayName()}</div>
                    </div>
                    
                    <Link 
                      to="/" 
                      className={cn(
                        "flex items-center space-x-3 p-4 rounded-lg hover:bg-muted transition-colors",
                        location.pathname === '/' && "bg-primary/10 text-primary border border-primary/20"
                      )}
                    >
                      <Home className={cn("h-5 w-5", location.pathname === '/' && "text-primary")} />
                      <span className="font-medium">Home</span>
                    </Link>
                    
                    <Link 
                      to="/log-game" 
                      className={cn(
                        "flex items-center space-x-3 p-4 rounded-lg hover:bg-muted transition-colors",
                        location.pathname === '/log-game' && "bg-primary/10 text-primary border border-primary/20"
                      )}
                    >
                      <PenLine className={cn("h-5 w-5", location.pathname === '/log-game' && "text-primary")} />
                      <span className="font-medium">Log Game</span>
                    </Link>

                    <Link 
                      to="/tournaments" 
                      className={cn(
                        "flex items-center space-x-3 p-4 rounded-lg hover:bg-muted transition-colors",
                        location.pathname.includes('/tournament') && "bg-primary/10 text-primary border border-primary/20"
                      )}
                    >
                      <Trophy className={cn("h-5 w-5", location.pathname.includes('/tournament') && "text-primary")} />
                      <span className="font-medium">Tournaments</span>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className={cn(
                        "flex items-center space-x-3 p-4 rounded-lg hover:bg-muted transition-colors",
                        location.pathname === '/login' && "bg-primary/10 text-primary border border-primary/20"
                      )}
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="font-medium">Login</span>
                    </Link>
                  </>
                )}
              </div>

              {user && (
                <button 
                  className="flex items-center space-x-3 p-4 mt-auto text-destructive hover:bg-destructive/10 rounded-lg transition-colors border border-destructive/20"
                  onClick={handleLogout}
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Logout</span>
                </button>
              )}
            </div>
          </div>

          <div className="hidden md:flex space-x-2">
            {user ? (
              <>
                <Link to="/">
                  <Button 
                    variant={location.pathname === '/' ? 'default' : 'ghost'}
                    size="sm"
                    className={location.pathname === '/' ? 'bg-primary hover:bg-primary/90 shadow-sm' : 'hover:bg-primary/10'}
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Home
                  </Button>
                </Link>
                
                <Link to="/log-game">
                  <Button 
                    variant={location.pathname === '/log-game' ? 'default' : 'ghost'}
                    size="sm"
                    className={location.pathname === '/log-game' ? 'bg-primary hover:bg-primary/90 shadow-sm' : 'hover:bg-primary/10'}
                  >
                    <PenLine className="h-4 w-4 mr-2" />
                    Log Game
                  </Button>
                </Link>

                <Link to="/tournaments">
                  <Button 
                    variant={location.pathname.includes('/tournament') ? 'default' : 'ghost'}
                    size="sm"
                    className={location.pathname.includes('/tournament') ? 'bg-primary hover:bg-primary/90 shadow-sm' : 'hover:bg-primary/10'}
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    Tournaments
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary/90 shadow-sm">Login</Button>
              </Link>
            )}
          </div>
        </div>
      </nav>
      
      {/* Bottom Navigation for Mobile */}
      {user && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background/95 to-background/90 backdrop-blur-md border-t border-border md:hidden z-50 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] pb-safe pl-safe pr-safe">
          <div className="flex items-center justify-around h-16 px-6 py-2 max-w-md mx-auto">
            <Link to="/" className="flex flex-col items-center justify-center flex-1 py-1">
              <div className={cn(
                "p-2 rounded-full transition-colors",
                location.pathname === '/' ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"
              )}>
                <Home className="h-5 w-5" />
              </div>
              <span className={cn(
                "text-xs mt-1 font-medium",
                location.pathname === '/' && "text-primary"
              )}>Home</span>
            </Link>
            
            <Link to="/log-game" className="flex flex-col items-center justify-center flex-1 py-1">
              <div className={cn(
                "p-2 rounded-full transition-colors",
                location.pathname === '/log-game' ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"
              )}>
                <PenLine className="h-5 w-5" />
              </div>
              <span className={cn(
                "text-xs mt-1 font-medium",
                location.pathname === '/log-game' && "text-primary"
              )}>Log Game</span>
            </Link>
            
            <Link to="/tournaments" className="flex flex-col items-center justify-center flex-1 py-1">
              <div className={cn(
                "p-2 rounded-full transition-colors",
                location.pathname.includes('/tournament') ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"
              )}>
                <Trophy className="h-5 w-5" />
              </div>
              <span className={cn(
                "text-xs mt-1 font-medium",
                location.pathname.includes('/tournament') && "text-primary"
              )}>Tournaments</span>
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation; 